import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { siteConfig } from '@/mocks/siteConfig';
import { categories } from '@/mocks/categories';

const aboutBgImage = 'https://readdy.ai/api/search-image?query=Modern%20corporate%20office%20interior%20with%20warm%20lighting%2C%20clean%20minimalist%20design%2C%20glass%20walls%20and%20green%20plants%2C%20professional%20atmosphere%2C%20soft%20shadows%2C%20elegant%20workspace%20photography%20style&width=1800&height=600&seq=about-hero-bg&orientation=landscape';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const form = e.currentTarget;
    const honeypotInput = form.querySelector<HTMLInputElement>('input[name="mobile_alt"]');
    if (honeypotInput && honeypotInput.value.trim() !== '') {
      setFormStatus('success');
      return;
    }

    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9n1atobdkpk99j7gfeg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed && parsed.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg =
          parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || '';
        if (serverMsg.includes('spam')) {
          setFormError('提交失败，请稍后重试。');
        } else {
          setFormError(serverMsg || '提交失败，请稍后重试。');
        }
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查网络连接后重试。');
      setFormStatus('error');
    }
  };

  const schemaData = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        name: siteConfig.name,
        url: siteConfig.url,
        description: siteConfig.description,
        email: siteConfig.email,
        telephone: siteConfig.phone,
        address: {
          '@type': 'PostalAddress',
          streetAddress: '望京科技园A座18层',
          addressLocality: '北京',
          addressRegion: '朝阳区',
          addressCountry: 'CN',
        },
        foundingDate: siteConfig.founded,
        sameAs: [siteConfig.socialMedia.weibo],
      },
      {
        '@type': 'WebPage',
        '@id': `${siteConfig.url}/about#webpage`,
        url: `${siteConfig.url}/about`,
        name: `关于我们 - ${siteConfig.name}`,
        description: `${siteConfig.name}品牌介绍、公司声誉和联系方式。${siteConfig.description}`,
        inLanguage: 'zh-CN',
        isPartOf: { '@id': `${siteConfig.url}/#website` },
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: siteConfig.url },
          { '@type': 'ListItem', position: 2, name: '关于我们' },
        ],
      },
    ],
  };

  const milestones = [
    { year: '2018', title: '公司成立', desc: '苹果视频在北京正式成立，创始团队来自国内顶级互联网公司，致力于打造最好的免费影视平台。' },
    { year: '2019', title: '用户突破100万', desc: '凭借海量免费资源和流畅播放体验，上线一年注册用户突破100万。' },
    { year: '2020', title: '内容全面升级', desc: '与超过200家影视制作公司达成合作，平台视频资源突破5000部，涵盖全部主流分类。' },
    { year: '2022', title: '技术革新', desc: '自主研发视频压缩技术，实现4K高清在线播放不缓冲，用户体验大幅提升。' },
    { year: '2024', title: '用户突破5000万', desc: '成为国内领先的免费影视平台，移动端APP同步上线iOS和Android。' },
    { year: '2026', title: '用户突破1亿', desc: '全球注册用户突破1亿，正式迈入亿级平台行列，开启全新发展阶段。' },
  ];

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />
      <Navbar />

      <main className="pt-16">
        <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />

        {/* Hero */}
        <section className="relative w-full h-[300px] md:h-[400px] overflow-hidden">
          <img
            src={aboutBgImage}
            alt="关于苹果视频"
            className="absolute inset-0 w-full h-full object-cover"
            title="苹果视频 - 品牌介绍"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/50"></div>
          <div className="absolute inset-0 flex flex-col items-center justify-center px-4">
            <h1 className="text-2xl md:text-4xl font-heading font-bold text-background-50 mb-3">
              关于苹果视频
            </h1>
            <p className="text-sm md:text-base text-background-200 max-w-lg text-center leading-relaxed">
              让每个人都能免费享受高品质影视内容的乐趣
            </p>
          </div>
        </section>

        {/* Brand Introduction */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-3">
                品牌介绍
              </h2>
              <p className="text-sm text-foreground-500 max-w-xl mx-auto">
                了解苹果视频的创立故事和发展历程
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
              <div>
                <h3 className="text-base font-heading font-semibold text-foreground-950 mb-4">
                  我们的使命
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  苹果视频成立于2018年，是国内领先的免费影视在线播放平台。我们的使命是让每个人都能轻松享受高品质的影视内容，无需付费，无需会员，免费看个够。
                </p>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  我们坚信，好的影视内容应该是人人可及的。通过技术创新和资源整合，苹果视频为用户提供了海量的电影、电视剧、综艺、动漫和纪录片资源，覆盖了各种类型和题材，满足不同用户的观影需求。
                </p>
              </div>
              <div>
                <h3 className="text-base font-heading font-semibold text-foreground-950 mb-4">
                  核心竞争力
                </h3>
                <ul className="flex flex-col gap-3">
                  {[
                    { icon: 'ri-database-2-line', title: '海量资源', desc: '与超过200家影视公司合作，资源库超过10000部' },
                    { icon: 'ri-sd-card-line', title: '高清画质', desc: '自主研发压缩技术，4K画质流畅不缓冲' },
                    { icon: 'ri-device-line', title: '多端支持', desc: 'PC、手机、平板、电视全平台无缝观影' },
                    { icon: 'ri-shield-check-line', title: '安全可靠', desc: '严格内容审核，绿色健康的观影环境' },
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className={`${item.icon} text-primary-600 text-sm`}></i>
                      </div>
                      <div>
                        <strong className="block text-sm text-foreground-800 font-medium">
                          {item.title}
                        </strong>
                        <span className="text-xs text-foreground-500">{item.desc}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Milestones */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-3">
                发展历程
              </h2>
              <p className="text-sm text-foreground-500">
                从初创到亿级平台，每一步都脚踏实地
              </p>
            </div>

            <div className="relative">
              <div className="absolute left-3 md:left-1/2 top-0 bottom-0 w-0.5 bg-background-300/60 -translate-x-1/2 hidden md:block"></div>

              <div className="flex flex-col gap-6">
                {milestones.map((m, idx) => (
                  <div
                    key={idx}
                    className={`flex flex-col md:flex-row items-start gap-4 md:gap-8 ${
                      idx % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                    }`}
                  >
                    <div className={`flex-1 ${idx % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                      <div className="bg-background-50 border border-background-200/70 rounded-lg p-5">
                        <span className="inline-block px-3 py-1 text-xs font-bold bg-accent-100 text-accent-700 rounded-full mb-2">
                          {m.year}
                        </span>
                        <h3 className="text-sm font-heading font-semibold text-foreground-950 mb-1">
                          {m.title}
                        </h3>
                        <p className="text-xs text-foreground-500 leading-relaxed">
                          {m.desc}
                        </p>
                      </div>
                    </div>

                    <div className="hidden md:flex w-8 items-center justify-center flex-shrink-0">
                      <div className="w-3 h-3 rounded-full bg-primary-500 border-2 border-background-100 z-10"></div>
                    </div>

                    <div className="flex-1 hidden md:block"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Reputation */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-3">
                品牌声誉
              </h2>
              <p className="text-sm text-foreground-500">
                用户和行业的认可，是我们前进的最大动力
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
              {[
                { number: '1亿+', label: '全球注册用户' },
                { number: '10000+', label: '影视资源' },
                { number: '99.7%', label: '用户满意度' },
                { number: '200+', label: '合作伙伴' },
              ].map((stat, idx) => (
                <div
                  key={idx}
                  className="text-center p-6 rounded-lg bg-background-100 border border-background-200/70"
                >
                  <div className="text-2xl md:text-3xl font-heading font-bold text-primary-500 mb-1">
                    {stat.number}
                  </div>
                  <div className="text-xs text-foreground-500">{stat.label}</div>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {[
                {
                  text: '"苹果视频是我用过的最好用的免费影视平台，资源丰富，播放流畅，真心推荐给所有喜欢看电影的朋友。"',
                  author: '张先生',
                  role: '资深影迷',
                },
                {
                  text: '"作为学生党，苹果视频真的太好了。不用花一分钱就能看到最新的电影和电视剧，画质也很棒。"',
                  author: '李同学',
                  role: '大学生用户',
                },
                {
                  text: '"从2019年就开始用苹果视频了，看着它一步步发展壮大，资源越来越多，体验越来越好，真的很感慨。"',
                  author: '王女士',
                  role: '老用户 · 使用5年',
                },
              ].map((testimonial, idx) => (
                <div
                  key={idx}
                  className="p-5 rounded-lg bg-background-100 border border-background-200/70"
                >
                  <div className="w-6 h-6 mb-3 flex items-center justify-center">
                    <i className="ri-double-quotes-l text-primary-300 text-xl"></i>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    {testimonial.text}
                  </p>
                  <div>
                    <p className="text-sm font-medium text-foreground-800">
                      {testimonial.author}
                    </p>
                    <p className="text-xs text-foreground-400">{testimonial.role}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-3">
                联系我们
              </h2>
              <p className="text-sm text-foreground-500">
                有任何问题或建议？我们很乐意听取您的意见
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div>
                <h3 className="text-base font-heading font-semibold text-foreground-950 mb-5">
                  联系方式
                </h3>
                <ul className="flex flex-col gap-4">
                  <li className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-primary-600 text-lg"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-800">电子邮箱</p>
                      <p className="text-xs text-foreground-500">{siteConfig.email}</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-phone-line text-primary-600 text-lg"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-800">客服电话</p>
                      <p className="text-xs text-foreground-500">{siteConfig.phone}</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-line text-primary-600 text-lg"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-800">公司地址</p>
                      <p className="text-xs text-foreground-500">{siteConfig.address}</p>
                    </div>
                  </li>
                </ul>

                <div className="mt-6 p-5 rounded-lg bg-background-50 border border-background-200/70">
                  <h4 className="text-sm font-heading font-semibold text-foreground-950 mb-3">
                    社交媒体
                  </h4>
                  <div className="flex items-center gap-3">
                    <a
                      href={siteConfig.socialMedia.weibo}
                      target="_blank"
                      rel="nofollow noopener noreferrer"
                      className="flex items-center gap-2 px-4 py-2 text-xs font-medium rounded-full bg-background-100 text-foreground-600 hover:text-primary-500 hover:bg-primary-50 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-weibo-line"></i> 微博
                    </a>
                    <span className="text-xs text-foreground-500">
                      微信：{siteConfig.socialMedia.wechat}
                    </span>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-base font-heading font-semibold text-foreground-950 mb-5">
                  发送消息
                </h3>

                {formStatus === 'success' ? (
                  <div className="p-8 rounded-lg bg-background-50 border border-background-200/70 text-center">
                    <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-100">
                      <i className="ri-check-line text-primary-600 text-2xl"></i>
                    </div>
                    <p className="text-sm font-medium text-foreground-800 mb-1">
                      消息已发送成功！
                    </p>
                    <p className="text-xs text-foreground-500">
                      感谢您的留言，我们会在1-2个工作日内回复您。
                    </p>
                  </div>
                ) : (
                  <form
                    data-readdy-form
                    action="https://readdy.ai/api/form/d9n1atobdkpk99j7gfeg"
                    method="POST"
                    onSubmit={handleSubmit}
                    className="flex flex-col gap-4"
                  >
                    <div className="honeypot-field">
                      <input
                        type="text"
                        name="mobile_alt"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                      />
                    </div>

                    <div>
                      <label htmlFor="contact-name" className="block text-xs font-medium text-foreground-700 mb-1.5">
                        您的姓名
                      </label>
                      <input
                        id="contact-name"
                        type="text"
                        name="name"
                        required
                        placeholder="请输入您的姓名"
                        className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg text-foreground-700 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 transition-all"
                      />
                    </div>

                    <div>
                      <label htmlFor="contact-email" className="block text-xs font-medium text-foreground-700 mb-1.5">
                        电子邮箱
                      </label>
                      <input
                        id="contact-email"
                        type="email"
                        name="email"
                        required
                        placeholder="请输入您的邮箱"
                        className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg text-foreground-700 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 transition-all"
                      />
                    </div>

                    <div>
                      <label htmlFor="contact-subject" className="block text-xs font-medium text-foreground-700 mb-1.5">
                        主题
                      </label>
                      <input
                        id="contact-subject"
                        type="text"
                        name="subject"
                        required
                        placeholder="请输入消息主题"
                        className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg text-foreground-700 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 transition-all"
                      />
                    </div>

                    <div>
                      <label htmlFor="contact-message" className="block text-xs font-medium text-foreground-700 mb-1.5">
                        消息内容
                      </label>
                      <textarea
                        id="contact-message"
                        name="message"
                        required
                        maxLength={500}
                        rows={4}
                        placeholder="请输入您的消息（最多500字）"
                        className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg text-foreground-700 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 transition-all resize-none"
                      ></textarea>
                    </div>

                    {formStatus === 'error' && (
                      <div className="p-3 rounded-lg bg-primary-50 border border-primary-200">
                        <p className="text-xs text-primary-700">{formError}</p>
                      </div>
                    )}

                    <button
                      type="submit"
                      className="px-6 py-3 text-sm font-medium bg-primary-500 text-background-50 dark:text-foreground-950 rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      发送消息
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Quick Links */}
        <section className="w-full px-4 md:px-6 py-8 md:py-10 bg-background-50 border-t border-background-200/70">
          <div className="max-w-4xl mx-auto">
            <h3 className="text-sm font-heading font-semibold text-foreground-950 mb-4">
              浏览视频分类
            </h3>
            <div className="flex items-center gap-3 flex-wrap">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className="flex items-center gap-2 px-4 py-2 rounded-full bg-background-100 border border-background-200/70 text-sm text-foreground-600 hover:border-primary-200 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className={`${cat.icon} text-xs`}></i>
                  </div>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}